import * as v from 'valibot';

// ============================================================================
// Action Button Types (Shared across all templates)
// ============================================================================

/**
 * Unified button variant schema for all templates
 * Matches the Button component variants: gradient, solid, outline, text
 */
export const ButtonVariantSchema = v.picklist(['solid', 'outline', 'text', 'gradient']);

export type ButtonVariant = v.InferOutput<typeof ButtonVariantSchema>;

export enum ActionStepType {
  CUSTOM_HANDLE = 'CUSTOM_HANDLE',
  API_CALL = 'API_CALL',
}

export enum APIMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

export const ActionStepSchema = v.object({
  type: v.picklist(Object.values(ActionStepType)),
  api: v.optional(
    v.object({
      method: v.optional(v.picklist(Object.values(APIMethod))),
      url: v.string(),
      body: v.optional(v.record(v.string(), v.unknown())),
    }),
  ),
});

export type ActionStep = v.InferOutput<typeof ActionStepSchema>;

export const ActionStepsSchema = v.array(ActionStepSchema);
export type ActionSteps = v.InferOutput<typeof ActionStepsSchema>;

export const ActionPayloadSchema = v.intersect([
  v.object({
    actionSteps: v.optional(ActionStepsSchema),
  }),
  v.record(v.string(), v.unknown()),
]);
export type ActionPayload = v.InferOutput<typeof ActionPayloadSchema>;

/**
 * Unified action button schema for all templates
 * Accommodates both flights and hotels requirements
 */
export const ActionButtonSchema = v.object({
  // Label text (e.g., "VIEW DETAILS", "PAY NOW", "CONTINUE")
  label: v.string(),

  // Action type identifier (e.g., "view_details", "pay_now", "continue")
  actionType: v.string(),

  // Button styling variant (optional to accommodate both use cases)
  buttonVariant: v.optional(ButtonVariantSchema),

  // Legacy variant field (kept for backwards compatibility with existing flight data)
  variant: v.optional(v.picklist(['primary', 'secondary', 'ghost'])),

  // Action payload (flexible structure to accommodate both use cases)
  actionPayload: v.optional(v.record(v.string(), v.unknown())), // TODO: Replace with ActionPayloadSchema

  // Optional: Is this button disabled?
  disabled: v.optional(v.boolean()),
  leadingIcon: v.optional(v.string()),
  trailingIcon: v.optional(v.string()),
});

export type ActionButton = v.InferOutput<typeof ActionButtonSchema>;
